/**
 * Dữ liệu mẫu và máy chủ giả của màn Khoá API (G2-D1-18, ADR-0068).
 *
 * Đóng vai tenant-service khi chưa đăng nhập Keycloak thật hoặc khi URL có
 * `?mock=`. Máy chủ giả **từ chối y như hợp đồng** — cùng mã HTTP, cùng
 * `pointer` — để nhánh lỗi chạy được khi chưa có backend:
 *
 * - Tạo khoá có quyền `ekyc:profile:export`: `422 PERMISSION_NOT_AVAILABLE`
 *   trỏ `/scopes/{i}` — diễn tình huống module vừa bị gỡ khỏi tổ chức.
 * - Tổ chức đã có 50 khoá còn hiệu lực: `422 API_KEY_QUOTA_EXCEEDED`.
 * - Thu hồi **Đối tác đã ngừng hợp tác** (`k-404`): `404` — diễn tình huống
 *   khoá vừa bị xoá ở phiên khác.
 *
 * Có đủ nhánh xấu: khoá hết hạn, khoá đã thu hồi, khoá chưa dùng lần nào, tên
 * rất dài, khoá không còn quyền nào (module đã gỡ).
 *
 * ⚠️ Tầng này bị xoá khi API thật về. Không quyết màu hay nhãn ở đây.
 */
import type { ApiError } from '../lib/errors';
import type { ApiKey, ApiKeyCreate, ApiKeyCreated, ApiKeyPage } from '../modules/khoa-api/types';
import { DANH_MUC_QUYEN } from './vai-tro';

const loi = (code: string, status: number, message: string, details?: unknown): ApiError => ({
  code,
  status,
  message,
  details,
});

const NGAY = 24 * 60 * 60 * 1000;
const luc = (soNgay: number) => new Date(Date.now() + soNgay * NGAY).toISOString();

const khoa = (p: Partial<ApiKey> & Pick<ApiKey, 'id' | 'name' | 'key_prefix' | 'scopes'>): ApiKey => ({
  status: 'ACTIVE',
  expires_at: null,
  created_by: 'u-004',
  created_at: luc(-30),
  revoked_at: null,
  last_used_at: null,
  ...p,
});

let dsKhoa: ApiKey[] = [
  khoa({
    id: 'k-001',
    name: 'Cổng dịch vụ công tỉnh',
    key_prefix: 'npk_Q7mZ2xLp',
    scopes: ['ocr:document:read', 'ocr:document:upload'],
    expires_at: luc(70),
    created_at: luc(-20),
    last_used_at: luc(-0.01),
  }),
  khoa({
    id: 'k-002',
    name: 'Phần mềm văn thư',
    key_prefix: 'npk_a9Kd0wVe',
    scopes: ['kb:document:read', 'kb:document:upload', 'assistant:chat:use', 'ocr:document:read'],
    created_at: luc(-12),
  }),
  khoa({
    id: 'k-003',
    name: 'Hệ thống quản lý hồ sơ một cửa của Sở Kế hoạch và Đầu tư, kết nối liên thông với cổng quốc gia',
    key_prefix: 'npk_Rr5tYb1N',
    scopes: ['ekyc:profile:read'],
    expires_at: luc(300),
    created_at: luc(-8),
    last_used_at: luc(-2),
  }),
  khoa({
    id: 'k-004',
    name: 'Kế toán cũ',
    key_prefix: 'npk_Hh3jK8sQ',
    scopes: ['ocr:document:read'],
    status: 'EXPIRED',
    expires_at: luc(-5),
    created_at: luc(-95),
    last_used_at: luc(-6),
  }),
  khoa({
    id: 'k-005',
    name: 'Thử nghiệm tích hợp RPA',
    key_prefix: 'npk_Zx0cVb9M',
    // Module rpa đã gỡ khỏi tổ chức: quyền vẫn ghi trên khoá nhưng Gateway không cho dùng.
    scopes: ['rpa:flow:run'],
    status: 'REVOKED',
    created_at: luc(-60),
    revoked_at: luc(-40),
  }),
  khoa({
    id: 'k-404',
    name: 'Đối tác đã ngừng hợp tác',
    key_prefix: 'npk_Pp4oIu7Y',
    scopes: ['ocr:document:read'],
    created_at: luc(-3),
  }),
];

const TOI_DA = 50;

function biMat(): string {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  let s = '';
  for (const x of b) s += String.fromCharCode(x);
  return `npk_${btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}`;
}

export const mayChuKhoaApi = {
  danhSach({ limit }: { limit: number; cursor?: string }): ApiKeyPage {
    const sap = [...dsKhoa].sort((a, b) => b.created_at.localeCompare(a.created_at));
    return { items: sap.slice(0, limit), next_cursor: null };
  },

  tao(du: ApiKeyCreate): ApiKeyCreated {
    const ten = du.name.trim();
    if (ten === '') {
      throw loi('INVALID_ARGUMENT', 400, 'Dữ liệu gửi lên có 1 lỗi.', [
        { pointer: '#/name', detail: 'Tên khoá không được để trống. Ghi tên hệ thống sẽ dùng khoá, ví dụ Cổng dịch vụ công.' },
      ]);
    }
    // Như tenant-service: khoá API chỉ nhận quyền của module, không nhận quyền nền tảng `core` (ADR-0104)
    const coSan = new Set(DANH_MUC_QUYEN.filter((q) => q.module_code !== 'core').map((q) => q.code));
    coSan.delete('ekyc:profile:export');
    const sai = du.scopes
      .map((s, i) => [s, i] as const)
      .filter(([s]) => !coSan.has(s))
      .map(([, i]) => ({
        pointer: `#/scopes/${i}`,
        detail: 'Quyền này không thuộc module nào đang cài cho tổ chức. Bỏ quyền này hoặc cài module trước.',
      }));
    if (sai.length > 0) {
      throw loi(
        'PERMISSION_NOT_AVAILABLE',
        422,
        `Có ${sai.length} quyền không thuộc module nào đang cài cho tổ chức. Bỏ các quyền đó rồi tạo lại.`,
        sai,
      );
    }
    if (dsKhoa.filter((k) => k.status === 'ACTIVE').length >= TOI_DA) {
      throw loi(
        'API_KEY_QUOTA_EXCEEDED',
        422,
        `Tổ chức đã có ${TOI_DA} khoá API còn hiệu lực, là số tối đa. Thu hồi một khoá không còn dùng rồi tạo lại.`,
      );
    }
    const secret = biMat();
    const moi = khoa({
      id: `k-${Date.now()}`,
      name: ten,
      key_prefix: secret.slice(0, 12),
      scopes: [...new Set(du.scopes)],
      expires_at: du.expires_at ?? null,
      created_at: new Date().toISOString(),
    });
    dsKhoa = [moi, ...dsKhoa];
    return { api_key: moi, secret };
  },

  thuHoi(id: string): void {
    const k = dsKhoa.find((x) => x.id === id);
    if (!k || id === 'k-404') {
      throw loi('NOT_FOUND', 404, 'Không tìm thấy khoá API này trong tổ chức. Tải lại danh sách khoá.');
    }
    if (k.status === 'REVOKED') return;
    dsKhoa = dsKhoa.map((x) =>
      x.id === id ? { ...x, status: 'REVOKED', revoked_at: new Date().toISOString() } : x,
    );
  },
};
